import { useEffect, useState, useMemo } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Calendar,
  ArrowRight,
  ExternalLink,
  Play,
  Check,
  ArrowLeft,
  BellIcon,
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { DropdownMenu, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import img1 from '../../../public/Images/ClassThumbnail.png';
import { cn } from '../../lib/utils';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Fancybox from '../../lib/FancyBox';
import SyllabusModal from '../../components/modals/SyllabusModal';
import { TestScheduleSkeleton } from './TestScheduleSkeleton';

const CARD_STATES = {
  UPCOMING: 'upcoming',
  REMINDER_SET: 'reminder_set',
  LIVE_NOW: 'live_now',
  ENDED_RECORDING_SOON: 'ended_recording_soon',
  ENDED_RECORDING_AVAILABLE: 'ended_recording_available',
  EXPANDABLE_SYLLABUS: 'expandable_syllabus',
  LIVE_IN_PROGRESS: 'live_in_progress',
  ATTEMPTED_ANALYSIS: 'attempted_analysis',
  ATTEMPTED_SOLUTION: 'attempted_solution',
  ATTEMPT_TEST: 'attempt_test',
};

export function TestSchedule({ isExpanded = false, setIsExpanded }) {
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [expandedSyllabus, setExpandedSyllabus] = useState({});
  const [scheduleData, setScheduleData] = useState({ liveItems: [], monthlyItems: {} });
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);
  const [reminderStatus, setReminderStatus] = useState({});
  const [groups, setGroups] = useState([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(true);
  const navigate = useNavigate();
  const [showSyllabusDialog, setShowSyllabusDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState({ title: '', syllabus: '' });

  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');

  const openVideoModal = async (videoUrl, liveSessionId) => {
    setCurrentVideoUrl(videoUrl);
    setIsVideoModalOpen(true);

    setTimeout(() => {
      const el = document.getElementById('hidden-fancybox-trigger');
      if (el) el.click();
    }, 0);

    try {
      await axios.patch(`/api/calendar/${liveSessionId}/mark-watched`);
    } catch (error) {
      console.error('Failed to mark recording as watched:', error);
    }
  };

  const getYouTubeVideoId = (url) => {
    if (!url) return null;

    const cleanUrl = url.includes('https://www.youtube.com/watch?v=')
      ? url.split('https://www.youtube.com/watch?v=').pop()
      : url;

    const regExp = /^.*(http:\/\/googleusercontent.com\/youtube.com\/2\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = cleanUrl.match(regExp);

    return match && match[2] && match[2].length === 11 ? match[2] : cleanUrl.substring(0, 11);
  };

  const handleSetReminder = async (liveSessionId) => {
    try {
      await axios.get(`/api/calendar/${liveSessionId}/set-reminder`);
      setReminderStatus((prev) => ({ ...prev, [liveSessionId]: true }));
    } catch (error) {
      console.error('Failed to set reminder:', error);
    }
  };

  const processApiResponse = (apiData) => {
    if (!apiData) {
      console.error('Invalid API data structure:', apiData);
      return { liveItems: [], monthlyItems: {} };
    }

    const liveItems = [];
    const monthlyItems = {};
    const processedIds = new Set();
    const now = new Date();

    const createTransformedItem = (item, type) => {
      const startDate = new Date(item.startDate);
      if (isNaN(startDate.getTime())) {
        return null;
      }
      return {
        id: type === 'attempted' ? item.testId : item._id,
        packId: item.packId,
        testName: item.title,
        displayTitle: item.title,
        date: startDate.getDate().toString(),
        day: startDate.toLocaleDateString('en-US', { weekday: 'short' }),
        month: startDate.toLocaleDateString('en-US', { month: 'short' }),
        time: startDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
        startDateObj: startDate,
        icon: item?.exam?.icon,
        sessionLink: item?.sessionLink,
        isReminderSet: item?.isReminderSet,
        syllabusHTML: item?.syllabus,
        recordingVideoId: item?.recordingVideoId,
        endDateObj: item.endDate ? new Date(item.endDate) : startDate,
      };
    };

    const getPriority = (cardState, sessionType) => {
      if (cardState === CARD_STATES.LIVE_NOW && sessionType === 'video') return 1;
      if (cardState === CARD_STATES.ATTEMPT_TEST) return 2;
      if (cardState === CARD_STATES.ENDED_RECORDING_AVAILABLE) return 3;
      if (cardState === CARD_STATES.ATTEMPTED_ANALYSIS || cardState === CARD_STATES.ATTEMPTED_SOLUTION) return 4;
      return 5;
    };

    // 1. Process Live Sessions (currently live and ended with recording)
    if (Array.isArray(apiData.liveSessions)) {
      apiData.liveSessions.forEach((session) => {
        const startDate = new Date(session.startDate);
        const endDate = session.endDate ? new Date(session.endDate) : startDate;

        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          return;
        }

        const isLive = now >= startDate && now <= endDate;
        const hasEnded = now > endDate;

        if (isLive) {
          const transformed = {
            ...createTransformedItem(session, 'session'),
            liveSessionId: session._id,
            cardState: CARD_STATES.LIVE_NOW,
            sessionType: 'video',
            image: session.thumbnail && session.thumbnail !== '#' ? session.thumbnail : img1,
          };
          if (transformed) {
            transformed.priority = getPriority(transformed.cardState, transformed.sessionType);
            liveItems.push(transformed);
            processedIds.add(session._id);
          }
        } else if (hasEnded && session.recordingVideoId) {
          const transformed = {
            ...createTransformedItem(session, 'session'),
            liveSessionId: session._id,
            cardState: CARD_STATES.ENDED_RECORDING_AVAILABLE,
            sessionType: 'video',
            image: session.thumbnail && session.thumbnail !== '#' ? session.thumbnail : img1,
            recordingVideoId: session.recordingVideoId,
            endDateObj: endDate,
          };
          if (transformed) {
            transformed.priority = getPriority(transformed.cardState, transformed.sessionType);
            liveItems.push(transformed);
            processedIds.add(session._id);
          }
        }
      });
    }

    // 2. Process Live Tests
    if (Array.isArray(apiData.liveTests)) {
      apiData.liveTests.forEach((test) => {
        if (processedIds.has(test._id)) return;
        const transformed = {
          ...createTransformedItem(test, 'test'),
          testType: test?.exam?.shortName || 'Test',
          syllabus: 'SYLLABUS',
          syllabusHTML: test.syllabus,
          cardState: CARD_STATES.ATTEMPT_TEST,
          sessionType: 'test',
          image: test.thumbnail || img1,
          detailAccessDate: new Date(test.detailAccessDate),
          isResumeTest: test.isResumeTest,
        };
        if (transformed) {
          transformed.priority = getPriority(transformed.cardState, transformed.sessionType);
          liveItems.push(transformed);
          processedIds.add(test._id);
        }
      });
    }

    // Sort all live items together by priority, then by date
    liveItems.sort((a, b) => {
      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }
      return a.startDateObj - b.startDateObj;
    });

    // 3. Process other items for monthly view (including only upcoming sessions, as ended are now in liveItems)
    const monthlyApiItems = [
      ...(apiData.tests?.map((t) => ({ ...t.test, additionalTests: t.additionalTests, type: 'test' })) || []),
      ...(apiData.liveSessions
        ?.filter((s) => {
          const startDate = new Date(s.startDate);
          const endDate = s.endDate ? new Date(s.endDate) : startDate;
          return now < startDate; // Only include upcoming sessions here
        })
        .map((s) => ({ ...s, type: 'session' })) || []),
    ];

    if (apiData.attemptedTest) {
      monthlyApiItems.unshift({ ...apiData.attemptedTest, type: 'attempted' });
    }

    monthlyApiItems.forEach((item) => {
      const itemId = item.type === 'attempted' ? item.testId : item._id;
      if (processedIds.has(itemId)) {
        return;
      }

      const startDate = new Date(item.startDate);
      const endDate = item.endDate ? new Date(item.endDate) : startDate;

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return;
      }

      let transformed;
      if (item.type === 'test') {
        transformed = {
          ...createTransformedItem(item, 'test'),
          testType: item?.exam?.shortName || 'Test',
          syllabus: 'SYLLABUS',
          syllabusHTML: item.syllabus,
          cardState: CARD_STATES.EXPANDABLE_SYLLABUS,
          sessionType: 'test',
          image: item.thumbnail || img1,
          additionalTests: item.additionalTests,
          detailAccessDate: new Date(item.detailAccessDate),
        };
      } else if (item.type === 'session') {
        transformed = {
          ...createTransformedItem(item, 'session'),
          liveSessionId: item._id,
          cardState: CARD_STATES.UPCOMING,
          sessionType: 'video',
          image: item.thumbnail && item.thumbnail !== '#' ? item.thumbnail : img1,
          isReminderSet: item.isReminderSet,
          sessionLink: item.sessionLink,
        };
      } else if (item.type === 'attempted') {
        transformed = {
          ...createTransformedItem(item, 'attempted'),
          time: ' ',
          testType: 'Test',
          syllabus: 'SYLLABUS',
          cardState: CARD_STATES.ATTEMPTED_SOLUTION, // This was the original, keeping it as is
          // Changing this to ATTEMPTED_ANALYSIS based on previous context, if it aligns with desired behavior
          // cardState: CARD_STATES.ATTEMPTED_ANALYSIS,
          sessionType: 'test',
          image: img1,
          score: item.totalMarkScored,
          scoreOutOf: item.totalMarks,
          detailAccessDate: item.detailAccessDate ? new Date(item.detailAccessDate) : undefined,
        };
      }

      if (transformed) {
        const monthYear = transformed.startDateObj.toLocaleString('en-US', { month: 'long', year: 'numeric' });
        if (!monthlyItems[monthYear]) monthlyItems[monthYear] = [];
        monthlyItems[monthYear].push(transformed);
        processedIds.add(itemId);
      }
    });

    for (const month in monthlyItems) {
      monthlyItems[month].sort((a, b) => {
        // Attempted items still get special sorting within their monthly view if needed
        if (a.cardState === CARD_STATES.ATTEMPTED_SOLUTION && b.cardState !== CARD_STATES.ATTEMPTED_SOLUTION) return -1;
        if (b.cardState === CARD_STATES.ATTEMPTED_SOLUTION && a.cardState !== CARD_STATES.ATTEMPTED_SOLUTION) return 1;
        return a.startDateObj - b.startDateObj;
      });
    }

    return { liveItems, monthlyItems };
  };
  const fetchSchedule = async (examId) => {
    if (!examId) return;

    setIsLoadingSchedule(true);
    setScheduleError(null);

    const now = new Date();
    const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
    const currentYear = now.getFullYear();
    const timelineStart = `${currentMonth}/${currentYear}`;

    const nextMonthDate = new Date(now);
    nextMonthDate.setMonth(now.getMonth() + 1);
    const nextMonth = String(nextMonthDate.getMonth() + 1).padStart(2, '0');
    const nextYear = nextMonthDate.getFullYear();
    const timelineEnd = `${nextMonth}/${nextYear}`;

    let apiUrl = `/api/calendar/schedule?timelineStart=${timelineStart}&timelineEnd=${timelineEnd}&examId=${examId}`;

    try {
      const response = await axios.get(apiUrl);
      if (response.data && response.data.success && response.data.data) {
        const processedData = processApiResponse(response?.data?.data);
        setScheduleData(processedData);
      } else {
        console.error('API call successful but data is not in expected format:', response.data);
        setScheduleData({ liveItems: [], monthlyItems: {} });
        setScheduleError(new Error('Invalid data format from API.'));
      }
    } catch (error) {
      console.error('Error fetching schedule data:', error);
      setScheduleError(error);
      setScheduleData({ liveItems: [], monthlyItems: {} });
    } finally {
      setIsLoadingSchedule(false);
    }
  };

  const fetchPacks = async () => {
    setIsLoadingGroups(true);
    let apiURL = `/api/user/profile/exams`;

    try {
      const response = await axios.get(apiURL);
      const exams = response?.data?.data?.exams || [];
      setGroups(exams);

      if (exams.length > 0) {
        const firstExamId = exams[0]._id;
        setSelectedFilter(firstExamId);
        fetchSchedule(firstExamId);
      }
    } catch (err) {
      console.error('Error fetching exams:', err);
    } finally {
      setIsLoadingGroups(false);
    }
  };

  useEffect(() => {
    fetchPacks();
  }, []);
  const headerInfo = useMemo(() => {
    const info = {
      liveTestTitle: null,
      upcomingTest: null,
    };

    // 1. Check for live tests
    if (scheduleData.liveItems && scheduleData.liveItems.length > 0) {
      info.liveTestTitle = scheduleData.liveItems[0].displayTitle;
      return info;
    }

    // 2. If no live tests, check for the next upcoming test
    if (scheduleData.monthlyItems) {
      const now = new Date();
      const allUpcomingItems = Object.values(scheduleData.monthlyItems)
        .flat()
        .filter((item) => item.startDateObj > now)
        .sort((a, b) => a.startDateObj - b.startDateObj);

      if (allUpcomingItems.length > 0) {
        const nextTest = allUpcomingItems[0];
        const diffTime = nextTest.startDateObj - now;
        const oneDay = 1000 * 60 * 60 * 24;
        const oneHour = 1000 * 60 * 60;
        const oneMinute = 1000 * 60;

        if (diffTime >= oneDay) {
          const diffDays = Math.ceil(diffTime / oneDay);
          info.upcomingTest = {
            title: nextTest.displayTitle,
            timeUntil: `${diffDays} day${diffDays > 1 ? 's' : ''}`,
          };
        } else if (diffTime >= oneHour) {
          const diffHours = Math.ceil(diffTime / oneHour);
          info.upcomingTest = {
            title: nextTest.displayTitle,
            timeUntil: `${diffHours} hour${diffHours > 1 ? 's' : ''}`,
          };
        } else if (diffTime > 0) {
          const diffMinutes = Math.ceil(diffTime / oneMinute);
          info.upcomingTest = {
            title: nextTest.displayTitle,
            timeUntil: `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`,
          };
        }
      }
    }

    return info;
  }, [scheduleData]);

  const handleExamSelection = (examId) => {
    setSelectedFilter(examId);
    fetchSchedule(examId);
  };

  const toggleExpanded = () => {
    if (setIsExpanded) {
      setIsExpanded(!isExpanded);
    }
  };

  const handleViewSyllabus = (title, syllabusHtml) => {
    setDialogContent({ title, syllabus: syllabusHtml });
    setShowSyllabusDialog(true);
  };

  const countTests = (month) => {
    return scheduleData.monthlyItems[month]?.length || 0;
  };

  const toggleSyllabus = (month, index) => {
    setExpandedSyllabus((prev) => {
      const key = `${month}-${index}`;
      return {
        ...prev,
        [key]: !prev[key],
      };
    });
  };

  const renderTestCard = (test, month, index) => {
    const syllabusKey = `${month}-${index}`;
    const isSyllabusExpanded = expandedSyllabus[syllabusKey] || false;
    const now = new Date();

    const AdditionalTestsDisplay = ({ count }) =>
      count > 0 && (
        <div className="mb-8 pl-15 text-xs text-[var(--q3-neutral-light)] font-medium -mt-2">
          And {count} more tests
        </div>
      );

    const DateDisplay = () => (
      <div
        className="flex flex-col items-center justify-center w-12 h-18 bg-[var(--q3-surface-default)] rounded-lg mr-3"
        style={{ boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)' }}
      >
        <span className="text-sm rounded-t-lg text-center text-[var(--q3-neutral-default)] bg-[var(--q3-surface-dimmer)] w-full h-full">
          {test.day}
        </span>
        <span className="text-lg font-medium mb-0 text-[var(--q3-neutral-default)]">{test.date}</span>
        <span className="text-sm text-[var(--q3-neutral-default)]">{test.month}</span>
      </div>
    );

    const LiveBadge = () => (
      <div className="flex text-xs font-bold flex-col items-center justify-center w-11 h-13 bg-[var(--q3-danger-normal)] rounded-lg mr-3 text-[var(--q3-neutral-default_inverted)]">
        <span>LIVE</span>
        <span>NOW</span>
      </div>
    );

    const CardHeader = () => (
      <div className="flex relative items-center justify-between p-4 -mb-1">
        <span className="h-8 flex text-sm border-[var(--q3-surface-dimmer)] border-r-1 border-l-1 border-b-1 px-1.5 pt-3 pb-1 rounded-md absolute -top-1.5 left-3">
          <span className="text-[var(--q3-neutral-light)] text-xs font-normal self-center">{test.time}</span>
        </span>

        <span className="h-8 flex items-center text-sm border-[var(--q3-surface-dimmer)] border-r-1 border-l-1 border-b-1 px-1.5 pt-3 pb-1 rounded-md absolute -top-1.5 right-3 gap-1">
          <img src={test?.icon} alt="exam icon" style={{ width: '15px' }} />
          <span className="text-[var(--q3-neutral-light)] text-xs font-normal">{test.testType}</span>
        </span>
      </div>
    );

    const ThumbnailMedia = () => (
      <div className="px-3 pt-1 relative text-[var(--q3-neutral-default)]">
        <img
          src={test.image}
          alt="Session thumbnail"
          className="w-full h-[160px] md:h-[130px] object-cover object-center rounded-lg"
        />
        {test.sessionType === 'video' && (
          <div className="absolute inset-0 flex items-center justify-center rounded-lg">
            <div className="text-center">
              <div className="bg-slate-600 rounded-full h-8 w-8 flex items-center justify-center mx-auto">
                <Play size={16} />
              </div>
            </div>
          </div>
        )}
      </div>
    );

    const TestTitle = () => <div className="font-medium text-sm text-[var(--q3-neutral-light)]">{test.testName}</div>;

    const EndedBadge = () => {
      const endedTime = test.endDateObj
        ? test.endDateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
        : '';
      return (
        <div className="bg-[var(--q3-neutral-light)] w-fit text-[var(--q3-neutral-default_inverted)] px-2.5 py-2 text-xs rounded-md inline-block">
          <span className="font-bold">ENDED</span> {endedTime && <span className="font-normal">at {endedTime}</span>}
        </div>
      );
    };

    const AttemptedBadge = () => (
      <div className="bg-[var(--q3-surface-default)] w-fit text-[var(--q3-success-normal)] p-2 text-sm font-medium rounded-lg flex items-center">
        <span className="mr-1">
          <Check size={16} strokeWidth={2.5} />{' '}
        </span>{' '}
        ATTEMPTED
      </div>
    );

    const SyllabusToggle = () =>
      test.syllabusHTML && (
        <div
          className="flex items-center text-[var(--q3-neutral-light)] text-xs font-medium cursor-pointer mb-1"
          onClick={() => toggleSyllabus(month, index)}
        >
          {test.syllabus}{' '}
          {isSyllabusExpanded ? <ChevronUp size={12} className="ml-1" /> : <ChevronDown size={12} className="ml-1" />}
        </div>
      );

    const SyllabusContent = () =>
      isSyllabusExpanded &&
      test.syllabusHTML && (
        <div
          className="syllabus-content bg-[var(--q3-surface-default)] p-3 rounded-md mb-3 text-xs text-[var(--q3-neutral-light)]"
          dangerouslySetInnerHTML={{ __html: test.syllabusHTML }}
        />
      );

    const ViewDetailsLink = () => (
      <div
        onClick={() => handleViewSyllabus(test.testName, test.syllabusHTML)}
        className="flex items-center text-[var(--q3-accent-normal)] text-xs cursor-pointer font-medium"
      >
        View Details <ArrowRight size={12} className="ml-1" />
      </div>
    );

    const ScoreDisplay = () => (
      <div className="text-sm font-medium">
        Score: {test.score} / {test?.scoreOutOf}
      </div>
    );

    switch (test.cardState) {
      case CARD_STATES.EXPANDABLE_SYLLABUS:
        return (
          <>
            <div className="flex mb-3">
              <DateDisplay />
              <div
                className="flex-1 overflow-hidden bg-[var(--q3-surface-default)] rounded-lg"
                style={{ boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)' }}
              >
                <CardHeader />
                <div className="p-3 pt-0 mt-1 flex flex-col gap-1">
                  <TestTitle />
                  {/* <SyllabusToggle /> */}
                  <SyllabusContent />
                  <ViewDetailsLink />
                </div>
              </div>
            </div>
            <AdditionalTestsDisplay count={test?.additionalTests} />
          </>
        );

      case CARD_STATES.UPCOMING:
        const isReminderSet = reminderStatus[test.liveSessionId] || test.isReminderSet;
        return (
          <div className="flex mb-3">
            <DateDisplay />
            <div
              className="flex-1 overflow-hidden rounded-lg bg-[var(--q3-surface-default)]"
              style={{ boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)' }}
            >
              <CardHeader />
              <div className="px-0">
                <div className="w-full ">
                  <ThumbnailMedia />
                </div>
                <div className="px-3 pb-3 mt-2">
                  <TestTitle />
                  <div className="mb-2 font-medium py-1">Orientation Session</div>
                  <Button
                    variant="outline"
                    className="w-full flex py-5 text-md"
                    onClick={() => handleSetReminder(test.liveSessionId)}
                    disabled={isReminderSet}
                  >
                    {isReminderSet ? (
                      <>You will be reminded.</>
                    ) : (
                      <>
                        <BellIcon className="mr-2 h-4 w-4" />
                        Set Reminder
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );

      case CARD_STATES.LIVE_NOW:
        return (
          <div className="flex mb-3">
            <LiveBadge />
            <div
              className="flex-1 overflow-hidden bg-[var(--q3-surface-default)] rounded-lg border-2 border-[var(--q3-danger-normal)]"
              style={{ boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)' }}
            >
              <CardHeader />
              <div className="px-0">
                <div className="w-full">
                  <ThumbnailMedia />
                </div>
                <div className="px-3 pb-3 flex gap-3 flex-col mt-2">
                  <TestTitle />
                  <Button
                    className="w-full bg-[var(--q3-danger-normal)] text-md py-5 flex items-center gap-2"
                    onClick={() => {
                      if (test.sessionLink) {
                        window.open(test.sessionLink, '_blank');
                      }
                    }}
                  >
                    Watch Live on Zoom <ExternalLink size={16} />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );

      case CARD_STATES.ENDED_RECORDING_AVAILABLE:
        return (
          <div className="mb-3 flex flex-col justify-start gap-2">
            <EndedBadge />
            <div className="flex">
              <div
                className="flex-1 overflow-hidden bg-[var(--q3-surface-default)] rounded-lg border"
                style={{ boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)' }}
              >
                <CardHeader />
                <div className="px-0">
                  <div className="w-full">
                    <ThumbnailMedia />
                  </div>
                  <div className="px-3 pb-3 flex flex-col gap-3 mt-2">
                    <TestTitle />
                    <Button
                      className="w-full py-5 bg-[var(--q3-surface-default)] border text-[var(--q3-accent-normal)] text-md mb-2 flex items-center gap-2"
                      onClick={() => openVideoModal(test.recordingVideoId, test.liveSessionId)} // Modified to pass liveSessionId
                    >
                      Watch Recording <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case CARD_STATES.ATTEMPTED_ANALYSIS:
        return (
          <>
            <div className="mb-3 flex flex-col justify-start gap-2">
              <AttemptedBadge />
              <div className="flex">
                <div
                  className="flex-1 overflow-hidden bg-[var(--q3-surface-default)] rounded-lg"
                  style={{ boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)' }}
                >
                  <CardHeader />
                  <div className="px-3 pt-1 ">
                    <div className="flex justify-between items-center mb-2">
                      <TestTitle />
                      <ScoreDisplay />
                    </div>
                    <Button
                      className="w-full bg-slate-800  mb-2 text-md py-5"
                      onClick={async () => {
                        try {
                          await axios.patch(`/api/calendar/${test.id}/markAnalysisViewed`);
                        } catch (error) {
                          console.error('Failed to mark analysis as viewed:', error);
                        }
                        navigate(`/${test.packId}/${test.id}/analysis?source=calendar`);
                      }}
                    >
                      View Analysis
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <AdditionalTestsDisplay count={test?.additionalTests} />
          </>
        );

      case CARD_STATES.ATTEMPTED_SOLUTION:
        return (
          <>
            <div className="mb-3 flex flex-col justify-start gap-2">
              <AttemptedBadge />
              <div className="flex">
                <div
                  className="flex-1 overflow-hidden bg-[var(--q3-surface-default)] rounded-lg"
                  style={{ boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)' }}
                >
                  <CardHeader />
                  <div className="px-3 pt-1 flex flex-col gap-2">
                    <div className="flex justify-between items-center mb-2">
                      <TestTitle />
                      <ScoreDisplay />
                    </div>
                    <div>
                      <div className="flex flex-col gap-2 mb-2">
                        <Button className="w-full bg-[var(--q3-accent-normal)]  text-md py-5 flex items-center gap-2">
                          View Analysis <ArrowRight size={16} />
                        </Button>
                        {test.detailAccessDate && now >= new Date(test.detailAccessDate) && (
                          <Button variant="outline" className="w-full text-md py-5">
                            View Solution
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <AdditionalTestsDisplay count={test?.additionalTests} />
          </>
        );

      case CARD_STATES.ATTEMPT_TEST:
        return (
          <>
            <div className="flex mb-3">
              <LiveBadge />
              <div
                className="flex-1 overflow-hidden bg-[var(--q3-surface-default)] rounded-lg  border-2 border-[var(--q3-danger-normal)]"
                style={{ boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)' }}
              >
                <CardHeader />
                <div className="px-3 pt-1 flex flex-col gap-3">
                  <TestTitle />
                  <span className="font-medium text-xs text-[var(--q3-neutral-light)]">Most Recent Test</span>
                  <div>
                    <Button
                      variant="outline"
                      className="w-full text-md mb-2 py-5 text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)]"
                      onClick={() => handleViewSyllabus(test.testName, test.syllabusHTML)}
                    >
                      View Syllabus & Details
                    </Button>
                    <Button
                      onClick={() => navigate(`/${test.packId}/${test.id}/details`)}
                      className="py-5 w-full bg-[var(--q3-danger-normal)] text-[var(--q3-neutral-default_inverted)] mb-2 text-md"
                    >
                      {test.isResumeTest ? 'Resume Test' : 'Attempt Test'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <AdditionalTestsDisplay count={test?.additionalTests} />
          </>
        );

      default:
        return null;
    }
  };

  if (isLoadingSchedule || isLoadingGroups) {
    return isExpanded ? <TestScheduleSkeleton /> : null;
  }

  return (
    <Card
      className={` shadow-none
    fixed right-0 bottom-0 w-full md:w-[320px] h-[100dvh] py-0 gap-0
    bg-[var(--q3-surface-dim)] z-10 
    ${isExpanded ? 'rounded-none' : 'rounded-none rounded-tl-2xl'}
    ${!isExpanded ? 'border-l-1' : 'border-l-2'} border-r-0
    flex flex-col
    transition-[height] duration-400 ease-in-out
    ${!isExpanded ? 'h-[60px]' : ''}
  `}
    >
      <CardHeader
        className={cn(
          'flex flex-col justify-center py-3 px-0 border-b-0 bg-[var(--q3-surface-dim)] sticky top-0 z-10',
          isExpanded ? 'rounded-none' : 'rounded-tl-2xl',
          !isExpanded && headerInfo.liveTestTitle && 'bg-[var(--q3-surface-dim)]',
        )}
      >
        <div
          className="flex gap-2 border-b-2 w-full px-3 pb-3 items-center relative cursor-pointer"
          onClick={toggleExpanded}
        >
          <ArrowLeft onClick={() => setIsExpanded(!isExpanded)} className="md:hidden cursor-pointer" />

          <CardTitle className="absolute left-1/2 -translate-x-1/2 text-lg font-bold text-[var(--q3-neutral-default)] md:static md:translate-x-0 md:left-auto whitespace-nowrap">
            Sessions & Tests Schedule
          </CardTitle>

          <Button className="hidden md:flex ml-auto" variant="ghost" size="icon">
            <div className="bg-[var(--q3-accent-light)] rounded-full p-1">
              {isExpanded ? (
                <ChevronDown size={18} color="var(--q3-accent-normal)" />
              ) : (
                <ChevronUp size={18} color="var(--q3-accent-normal)" />
              )}
            </div>
          </Button>
        </div>

        {isLoadingGroups ? (
          <div className="flex justify-center items-center py-4 px-3">
            <div className="text-sm text-[var(--q3-neutral-light)]">Loading exams...</div>
          </div>
        ) : (
          <div className="flex gap-2 mb-2 overflow-x-auto w-full px-3 py-2">
            {groups.map((each) => {
              const isSelected = selectedFilter === each?._id;
              return (
                <Badge
                  key={each?._id}
                  variant={isSelected ? 'default' : 'outline'}
                  className={`flex items-center gap-1 py-1 px-3 rounded-3xl cursor-pointer ${
                    isSelected
                      ? 'bg-[var(--q3-neutral-default)] text-[var(--q3-neutral-default_inverted)]'
                      : 'bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)]'
                  }`}
                  onClick={() => handleExamSelection(each?._id)}
                >
                  {each?._id !== 'all' && each?.icon && (
                    <span className="flex items-center justify-center text-sm leading-5 font-normal">
                      <img src={each?.icon} alt="examicons" style={{ width: '18px', height: 'auto' }} />
                    </span>
                  )}

                  <span className="flex items-center justify-center text-sm leading-5 font-normal">
                    <span>{each?.title}</span>
                  </span>
                  <span>{isSelected && <Check size={16} />}</span>
                </Badge>
              );
            })}
          </div>
        )}
      </CardHeader>
      {isExpanded ? (
        <CardContent
          className={`flex-1 h-fit pb-14 overflow-y-scroll duration-400  px-4 pt-0 ${!isExpanded ? 'hidden' : ''}`}
        >
          {isLoadingSchedule ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-sm text-[var(--q3-neutral-light)]">Loading schedule...</div>
            </div>
          ) : scheduleError ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-sm text-[var(--q3-danger-normal)]">Error loading schedule</div>
            </div>
          ) : Object.keys(scheduleData.liveItems).length === 0 &&
            Object.keys(scheduleData.monthlyItems).length === 0 ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-sm text-[var(--q3-neutral-light)]">No items scheduled</div>
            </div>
          ) : (
            <>
              {scheduleData.liveItems.length > 0 && (
                <div className="mb-4">
                  {scheduleData.liveItems.map((test, index) => renderTestCard(test, 'live', index))}
                </div>
              )}

              {Object.entries(scheduleData.monthlyItems).map(([month, tests]) => (
                <div key={month} className="mt-6">
                  <div className="flex items-center h-15 justify-between bg-[var(--q3-surface-dimmer)] p-4 rounded-lg my-6">
                    <div className="flex items-center gap-3">
                      <Calendar strokeWidth={1.5} className="h-5 w-5 mr-2 text-[var(--q3-neutral-default)]" />
                      <span className="font-medium text-[var(--q3-neutral-default)]">{month}</span>
                    </div>
                    <Badge
                      variant="outline"
                      className="bg-[var(--q3-surface-default_inverted)] rounded-4xl text-sm text-[var(--q3-neutral-light)] font-normal"
                    >
                      {countTests(month)} Items
                    </Badge>
                  </div>
                  {tests.map((test, index) => renderTestCard(test, month, index))}
                </div>
              ))}
            </>
          )}
        </CardContent>
      ) : (
        ''
      )}

      {/* Syllabus Dialog */}

      <SyllabusModal
        open={showSyllabusDialog}
        onOpenChange={() => setShowSyllabusDialog(false)}
        dialogContent={dialogContent}
      />

      {/* Fancybox component for video playback */}
      {isVideoModalOpen && (
        <Fancybox>
          <a
            href={`https://www.youtube.com/watch?v=${getYouTubeVideoId(currentVideoUrl)}`}
            data-fancybox
            data-type="iframe"
            style={{ display: 'none' }}
            id="hidden-fancybox-trigger"
          >
            Hidden Fancybox Trigger
          </a>
        </Fancybox>
      )}
    </Card>
  );
}
